import { GridReadyEvent } from "@ag-grid-community/core";
import { Component, TemplateRef, ViewChild } from "@angular/core";
import { AddonBinStationStatusEnum, ItemDetail, ItemDetailStatusEnum, JoDetail } from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

export interface UnapplyJodDialogParams extends DialogParams {
  joDetail: JoDetail; // assumes that its 'itemDetails' property is already populated.
}

@Component({
  templateUrl: "./unapply-jod-dialog.component.html",
  standalone: false,
})
export class UnapplyJodDialogComponent extends DialogBaseComponent<UnapplyJodDialogParams> {
  @ViewChild("unapplyCell") unapplyCell: TemplateRef<any>;
  joDetail: JoDetail;
  itemDetails: ItemDetail[];
  itdGridOptions: GridOptionsExt;
  isPageReady = false;

  constructor(
    public config: DynamicDialogConfig<UnapplyJodDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      header: "Items Applied to Job Order Detail",
      width: "830px",
      height: "510px",
    });

    this.prepare();
  }

  async prepare() {
    this.joDetail = this.params.joDetail;
    this.itdGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onItdGridReady,
    });
    // make sure all of the itemDetail data is available.
    await this.dbQueryService.getItemDetailsByJoDetailId(this.joDetail.id);
    this.itemDetails = this.joDetail.itemDetails;
    this.isPageReady = true;
  }

  onItdGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...AgFns.createCellButtonProps("Unapply", this.unapplyCell) },
      { headerName: "Id", field: "id", filter: "agTextColumnFilter" },
      { headerName: "Manufacturer", field: "product.productType.manufacturer.name", filter: "agTextColumnFilter" },
      { headerName: "Description", field: "product.productType.description", filter: "agTextColumnFilter" },
      { headerName: "Style", field: "product.productType.style", filter: "agTextColumnFilter" },
      { headerName: "Features", field: "product.featureChoicesExtract", filter: "agTextColumnFilter" },
    ];

    AgFns.initGrid(event, colDefs);
    AgFns.sizeColumnsToFit(this.itdGridOptions);
  }

  async unapplyItemDetail(itemDetail: ItemDetail) {
    this.unapplyOne(itemDetail);
    await this.saveAndUpdateUI();
  }

  async unapplyAll() {
    const itds = this.joDetail.itemDetails.slice();
    for (var itd of itds) {
      await this.unapplyOne(itd);
    }
    await this.saveAndUpdateUI();
  }

  private async saveAndUpdateUI() {
    await this.dbSaveService.saveChanges();
    this.itemDetails = this.joDetail.itemDetails;
    this.itdGridOptions.apiX.setGridOption("rowData", this.itemDetails);
  }

  private async unapplyOne(itd: ItemDetail) {
    const ok =
      itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingAddon ||
      itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingShipPrep;
    if (!ok) {
      this.toastr.warning("Item Id: " + itd.id + " could not be unapplied");
      return;
    }
    if (itd.itemDetailAddons.some((x) => x.addonBinStationStatusId != AddonBinStationStatusEnum.Pending)) {
      await this.dialogService.showOkMessage("Unable to unapply", "This item detail is currently part of an Addon or Ship Prep. workflow");
      return;
    }
    itd.joDetailId = null;
    itd.itemDetailStatusId = ItemDetailStatusEnum.InInventory;
    // remove all itemDetailAddons
    itd.itemDetailAddons.slice().forEach((itda) => {
      itda.entityAspect.setDeleted();
    });
  }

  close() {
    this.dialogRef.close(null);
  }
}
